import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, XIcon } from "lucide-react";
import { sanitizeNumericText, normalizeForEmit } from "@brewbase/lib";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface InlineEditableInputProps {
  value: string;
  onSave: (nextValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  /**
   * Numeric entry: routes input through the @brewbase/lib numeric-text helpers
   * (decimal/comma handling, blank, mobile keyboard) and saves a normalized
   * decimal string. Use this instead of type="number".
   */
  numeric?: boolean;
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
}

export function InlineEditableInput({
  value,
  onSave,
  placeholder,
  disabled,
  type = "text",
  numeric,
  className,
  inputClassName,
  autoFocus = false,
}: InlineEditableInputProps) {
  const isNumeric = numeric || type === "number";
  const [draft, setDraft] = useState<string>(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const isDirty = useMemo(() => draft !== (value ?? ""), [draft, value]);

  const handleCancel = () => {
    setDraft(value ?? "");
  };

  const handleSave = () => {
    if (!isDirty) return;
    onSave(isNumeric ? (normalizeForEmit(draft) ?? "") : draft);
  };

  const focusRef = useCallback(
    (ref: any) => {
      if (autoFocus && ref) {
        ref.focus();
      }
    },
    [autoFocus],
  );

  return (
    <div className={(className ?? "") + " flex items-center gap-2"}>
      <Input
        ref={focusRef}
        type={isNumeric ? "text" : type}
        inputMode={isNumeric ? "decimal" : undefined}
        value={draft}
        onChange={(e) =>
          setDraft(
            isNumeric ? sanitizeNumericText(e.target.value) : e.target.value,
          )
        }
        placeholder={placeholder}
        disabled={disabled}
        className={cn("w-full", inputClassName)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSave();
          }
          if (e.key === "Escape") {
            handleCancel();
          }
        }}
        onBlur={handleSave}
        autoFocus={autoFocus}
      />
      {isDirty && (
        <div className="flex">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={handleSave}
            disabled={disabled}
            className="size-7"
          >
            <CheckIcon className="size-3" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={handleCancel}
            disabled={disabled}
            className="size-7"
          >
            <XIcon className="size-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default InlineEditableInput;
